import React, { CSSProperties, useMemo } from 'react';
import { TextFieldProps, DeprecatedThemeOptions } from '@mui/material';
import {
  createTheme,
  StyledEngineProvider,
  Theme,
  ThemeProvider,
  TypographyStyle,
  useTheme,
} from '@mui/material/styles';
import { FONT_WEIGHT_MEDIUM } from '../../enums/FontType';
import { isVendorMode } from '../Vendor';
import { MindTickleThemeOptions } from '../../theme/MindTickleTheme';
import {
  OrganizationColorsSchema,
  OrganizationFontsSchema,
} from '../../definitions/schemas';

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

interface IContext {
  // flag that determines if there's a custom theme based on the organization's palette
  hasCustomTheme: boolean;
  // styles for non-themable components, such as the dialog from `useConfirm`
  buttonsStyles?: CSSProperties;
  // color prop to apply to themable inputs
  inputsColor: TextFieldProps['color'];
  // custom styles to apply to themable texts. Useful to overwrite the text styles
  // for non-themable components within deep levels of the components tree
  textStyles?: CSSProperties;
}

export const OrganizationThemeContext: React.Context<IContext> =
  React.createContext<IContext>({
    hasCustomTheme: false,
    buttonsStyles: undefined,
    inputsColor: 'secondary',
    textStyles: undefined,
  });

type Props = React.PropsWithChildren<{
  colors?: OrganizationColorsSchema;
  fonts?: OrganizationFontsSchema;
}>;

const OrganizationThemeContextProvider: React.FC<Props> = (props: Props) => {
  const theme = useTheme();

  const { children, colors, fonts } = props;

  const customTheme = useMemo(() => {
    if (!(colors || fonts)) {
      return;
    }

    /**
     * If this value is not defined even if the org hasn't defined custom
     * colors, the color is reset to MUI Theme default; so we need to define
     * at least the secondary as the default app's secondary
     */
    let palette: DeprecatedThemeOptions['palette'] = {
      secondary: theme.palette.secondary,
      // if iconColor flag is disabled, use secondary as default
      icon: theme.palette.icon,
    };

    if (colors) {
      const { primary, secondary, icon } = colors;

      palette = {
        primary: { main: primary },
        secondary: { main: secondary },
        icon: { main: icon },
      };
    }

    /**
     * Same fix for the font weight, because of the variants loaded at root.css
     * @see https://github.com/referenceable/referenceable_client/blob/develop/src/App.tsx#L183-L185
     */
    let typography: DeprecatedThemeOptions['typography'] = {
      fontWeightMedium: FONT_WEIGHT_MEDIUM,
    };

    if (fonts) {
      const { body, headline } = fonts;
      const defaultFontFamily = theme.typography.fontFamily;
      const defaultFontWeight = theme.typography.fontWeightRegular;

      const headlineStyles = {
        fontFamily: headline?.family
          ? `${headline.family}, ${defaultFontFamily}`
          : defaultFontFamily,
        fontWeight: headline?.weight ? +headline.weight : defaultFontWeight,
      } as TypographyStyle;

      typography = {
        fontFamily: body?.family
          ? `${body.family}, ${defaultFontFamily}`
          : defaultFontFamily,
        fontWeightRegular: body?.weight ? +body.weight : defaultFontWeight,
        h5: headlineStyles,
        h6: headlineStyles,
      };
    }

    return createTheme({
      components: isVendorMode() ? MindTickleThemeOptions.components : {},
      palette,
      typography,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colors, fonts]);

  const buttonsStyles = customTheme
    ? {
        backgroundColor: customTheme.palette.primary.main,
        color: customTheme.palette.primary.contrastText,
      }
    : undefined;

  const inputsColor: TextFieldProps['color'] = 'secondary';

  let textStyles: CSSProperties | undefined;
  if (customTheme) {
    textStyles = { color: colors?.secondary };
  }

  const context = {
    buttonsStyles,
    hasCustomTheme: !!(colors?.primary && colors?.secondary),
    inputsColor,
    textStyles,
  };

  const renderChildren = (): JSX.Element => {
    if (customTheme) {
      return (
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={customTheme}>{children}</ThemeProvider>
        </StyledEngineProvider>
      );
    }

    return <>{children}</>;
  };

  return (
    <OrganizationThemeContext.Provider value={context}>
      {renderChildren()}
    </OrganizationThemeContext.Provider>
  );
};

export default OrganizationThemeContextProvider;
